import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/SEO';
import { PageHero, Stepper, Button, Icon, TrustBar, Badge } from '../../components/ui';
import { useAuth } from '../../context/AuthContext';
import { trackDarshanClick } from '../../utils/analytics';
import {
  DARSHAN_TEMPLES, DARSHAN_SLOTS, AARTI_SLOTS, ID_TYPES, GUIDELINES, FAQ, BOOKING_TRUST, OFFICIAL_PORTAL,
} from '../../data/darshan';

const STEPS = ['Select Temple', 'Select Date & Time', 'Your Details', 'Confirm'];
const isoToday = () => new Date().toISOString().slice(0, 10);
const isoPlus = (days) => { const d = new Date(); d.setDate(d.getDate() + days); return d.toISOString().slice(0, 10); };
const fmtDate = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : '—');

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
};

export default function DarshanBooking() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [params] = useSearchParams();

  const initialTemple = DARSHAN_TEMPLES.find((x) => x.id === params.get('temple'))?.id || 'ram-mandir';
  const initialPersons = Math.min(8, Math.max(1, Number(params.get('persons')) || 1));
  const initialDate = params.get('date') && params.get('date') >= isoToday() ? params.get('date') : isoToday();

  const [step, setStep] = useState(0);
  const [templeId, setTempleId] = useState(initialTemple);
  const [kind, setKind] = useState('darshan'); // darshan | aarti
  const [date, setDate] = useState(initialDate);
  const [slot, setSlot] = useState('');
  const [persons, setPersons] = useState(initialPersons);
  const [details, setDetails] = useState({
    name: user ? `${user.firstName || user.name || ''} ${user.lastName || ''}`.trim() : '',
    phone: user?.phoneNumber || '',
    email: user?.email || '',
    idType: ID_TYPES[0],
  });
  const [errors, setErrors] = useState({});

  const temple = useMemo(() => DARSHAN_TEMPLES.find((x) => x.id === templeId), [templeId]);
  const slots = kind === 'aarti' ? AARTI_SLOTS.map((a) => `${a.name} · ${a.time}`) : DARSHAN_SLOTS;

  const validateDetails = () => {
    const e = {};
    if (!details.name.trim()) e.name = t('Name is required');
    if (!/^\d{10}$/.test(details.phone.replace(/\D/g, ''))) e.phone = t('Enter a valid 10-digit mobile number');
    if (details.email && !/\S+@\S+\.\S+/.test(details.email)) e.email = t('Enter a valid email address');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (step === 1 && !slot) { setErrors({ slot: t('Please choose a time slot') }); return; }
    if (step === 2 && !validateDetails()) return;
    setErrors({});
    setStep((s) => Math.min(3, s + 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const back = () => { setErrors({}); setStep((s) => Math.max(0, s - 1)); };
  const reset = () => { setStep(0); setSlot(''); setErrors({}); };

  const setD = (k) => (e) => { setDetails((p) => ({ ...p, [k]: e.target.value })); setErrors((p) => ({ ...p, [k]: undefined })); };

  return (
    <>
      <SEO
        title="Darshan Booking"
        description="Plan your darshan at Shri Ram Janmabhoomi Mandir, Hanuman Garhi, Kanak Bhawan and more. Free passes via the official Trust portal."
        canonicalUrl="/darshan-booking"
        keywords="Ram Mandir darshan booking, sugam darshan, aarti pass Ayodhya, darshan timings"
        structuredData={faqSchema}
      />
      <PageHero compact crumbs={[{ label: t('Home'), to: '/' }, { label: t('Booking') }, { label: t('Darshan Booking') }]} title={t('Darshan Booking')} subtitle={t('Choose a temple, pick your slot and get set for a blessed darshan')} />

      <div className="container booking-wrap">
        <div className="booking">
          <section className="card booking__panel" aria-live="polite">
            <Stepper steps={STEPS.map((s) => t(s))} current={step} />

            {step === 0 && (
              <>
                <h2 className="booking__step-title">{t('Select Temple')}</h2>
                <p className="booking__step-sub">{t('Pick the shrine you wish to visit. Ram Mandir passes are free and issued by the Trust.')}</p>
                <div className="pick-grid" role="radiogroup" aria-label={t('Select Temple')}>
                  {DARSHAN_TEMPLES.map((tp) => (
                    <button key={tp.id} type="button" role="radio" aria-checked={templeId === tp.id} className={`pick${templeId === tp.id ? ' is-selected' : ''}`} onClick={() => { setTempleId(tp.id); setSlot(''); }}>
                      <span className="pick__check"><Icon name="check" size={15} strokeWidth={2.5} /></span>
                      <span className="pick__media"><img src={tp.image} alt="" loading="lazy" /></span>
                      <span className="pick__body">
                        <span className="pick__name">{t(tp.name)}</span>
                        {i18n.language === 'en' && <span className="pick__hindi hindi">{tp.hindi}</span>}
                        <span className="pick__meta"><Icon name="clock" size={13} />{t(tp.timings)}</span>
                      </span>
                    </button>
                  ))}
                </div>
                <div className="booking__actions">
                  <span />
                  <Button onClick={next} icon={<Icon name="arrow-right" size={16} />}>{t('Continue')}</Button>
                </div>
              </>
            )}

            {step === 1 && (
              <>
                <h2 className="booking__step-title">{t('Select Date & Time')}</h2>
                <p className="booking__step-sub">{t('Slots shown are indicative temple timings. Final availability is confirmed by the temple.')}</p>
                <div className="form-grid">
                  <label className="field">
                    <span className="field__label">{t('Date of visit')}</span>
                    <span className="control"><Icon name="calendar" size={16} /><input type="date" min={isoToday()} max={isoPlus(30)} value={date} onChange={(e) => setDate(e.target.value)} /></span>
                    <span className="field__hint">{t('Bookings open up to 30 days ahead')}</span>
                  </label>
                  <div className="field">
                    <span className="field__label">{t('Devotees')}</span>
                    <div className="qty" aria-label={t('Devotees')}>
                      <button type="button" onClick={() => setPersons((p) => Math.max(1, p - 1))} disabled={persons <= 1} aria-label={t('Decrease')}><Icon name="minus" size={16} /></button>
                      <output>{persons}</output>
                      <button type="button" onClick={() => setPersons((p) => Math.min(8, p + 1))} disabled={persons >= 8} aria-label={t('Increase')}><Icon name="plus" size={16} /></button>
                    </div>
                    <span className="field__hint">{t('Up to 8 passes per mobile number')}</span>
                  </div>
                </div>

                {temple.pass === 'official' && (
                  <div className="field" style={{ marginTop: 18 }}>
                    <span className="field__label">{t('Type of visit')}</span>
                    <div className="slot-grid">
                      {[['darshan', t('Darshan')], ['aarti', t('Aarti pass')]].map(([id, label]) => (
                        <button key={id} type="button" className={`chip${kind === id ? ' is-active' : ''}`} onClick={() => { setKind(id); setSlot(''); }}>{label}</button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="field" style={{ marginTop: 18 }}>
                  <span className="field__label">{kind === 'aarti' ? t('Aarti') : t('Time slot')}</span>
                  <div className="slot-grid" role="radiogroup">
                    {slots.map((s) => (
                      <button key={s} type="button" role="radio" aria-checked={slot === s} className={`chip${slot === s ? ' is-active' : ''}`} onClick={() => { setSlot(s); setErrors({}); }}>
                        <Icon name="clock" size={14} />{s}
                      </button>
                    ))}
                  </div>
                  {errors.slot && <span className="field__error">{errors.slot}</span>}
                </div>

                <div className="booking__actions">
                  <Button variant="ghost" onClick={back} icon={<Icon name="arrow-left" size={16} />} iconPosition="left">{t('Back')}</Button>
                  <Button onClick={next} icon={<Icon name="arrow-right" size={16} />}>{t('Continue')}</Button>
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <h2 className="booking__step-title">{t('Your Details')}</h2>
                <p className="booking__step-sub">{t('We use these only to prepare your summary. Carry the same ID on the day.')}</p>
                <div className="form-grid">
                  <label className="field span-2">
                    <span className="field__label">{t('Full name')}</span>
                    <span className={`control${errors.name ? ' is-invalid' : ''}`}><Icon name="user" size={16} /><input value={details.name} onChange={setD('name')} placeholder={t('As on your ID')} autoComplete="name" /></span>
                    {errors.name && <span className="field__error">{errors.name}</span>}
                  </label>
                  <label className="field">
                    <span className="field__label">{t('Mobile number')}</span>
                    <span className={`control${errors.phone ? ' is-invalid' : ''}`}><Icon name="phone" size={16} /><input value={details.phone} onChange={setD('phone')} placeholder="98765 43210" inputMode="numeric" autoComplete="tel" /></span>
                    {errors.phone && <span className="field__error">{errors.phone}</span>}
                  </label>
                  <label className="field">
                    <span className="field__label">{t('Email (optional)')}</span>
                    <span className={`control${errors.email ? ' is-invalid' : ''}`}><Icon name="mail" size={16} /><input type="email" value={details.email} onChange={setD('email')} placeholder="you@example.com" autoComplete="email" /></span>
                    {errors.email && <span className="field__error">{errors.email}</span>}
                  </label>
                  <label className="field span-2">
                    <span className="field__label">{t('ID proof you will carry')}</span>
                    <span className="control control--select"><Icon name="badge-check" size={16} />
                      <select value={details.idType} onChange={setD('idType')}>{ID_TYPES.map((x) => <option key={x}>{x}</option>)}</select>
                    </span>
                  </label>
                </div>
                <div className="booking__actions">
                  <Button variant="ghost" onClick={back} icon={<Icon name="arrow-left" size={16} />} iconPosition="left">{t('Back')}</Button>
                  <Button onClick={next} icon={<Icon name="arrow-right" size={16} />}>{t('Review')}</Button>
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <h2 className="booking__step-title">{t('Confirm your plan')}</h2>
                <p className="booking__step-sub">{t('Review the details below.')}</p>
                <table className="summary-table">
                  <tbody>
                    <tr><th>{t('Temple')}</th><td>{t(temple.name)}</td></tr>
                    <tr><th>{t('Date')}</th><td>{fmtDate(date)}</td></tr>
                    <tr><th>{kind === 'aarti' ? t('Aarti') : t('Time slot')}</th><td>{slot}</td></tr>
                    <tr><th>{t('Devotees')}</th><td>{persons}</td></tr>
                    <tr><th>{t('Name')}</th><td>{details.name}</td></tr>
                    <tr><th>{t('Mobile')}</th><td>{details.phone}</td></tr>
                    <tr><th>{t('ID proof')}</th><td>{details.idType}</td></tr>
                  </tbody>
                </table>

                {temple.pass === 'official' ? (
                  <>
                    <div className="notice" style={{ marginTop: 18 }}>
                      <Icon name="info" size={18} />
                      <span>{t('Passes for Shri Ram Janmabhoomi Mandir are issued free of cost only by the Shri Ram Janmabhoomi Teerth Kshetra Trust. Continue on the official portal to complete your booking with the same details.')}</span>
                    </div>
                    <div className="booking__actions">
                      <Button variant="ghost" onClick={reset} icon={<Icon name="arrow-left" size={16} />} iconPosition="left">{t('Start over')}</Button>
                      <Button href={OFFICIAL_PORTAL} onClick={() => trackDarshanClick && trackDarshanClick('ram_mandir_official_portal')} icon={<Icon name="external-link" size={16} />}>{t('Continue on official portal')}</Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="notice notice--success" style={{ marginTop: 18 }}>
                      <Icon name="circle-check" size={18} />
                      <span>{t('No pass is required for this shrine. Visit during the timings shown and carry your ID. Your plan is ready!')}</span>
                    </div>
                    <div className="booking__actions">
                      <Button variant="ghost" onClick={reset} icon={<Icon name="arrow-left" size={16} />} iconPosition="left">{t('Start over')}</Button>
                      <Button to="/travel-guide" icon={<Icon name="arrow-right" size={16} />}>{t('Plan your day')}</Button>
                    </div>
                  </>
                )}
              </>
            )}
          </section>

          <aside className="card booking__summary" aria-label={t('Your selection')}>
            <div className="summary__media"><img src={temple.image} alt={temple.name} /></div>
            <div className="summary__body">
              <div>
                <div className="summary__title">{t(temple.name)}</div>
                {i18n.language === 'en' && <div className="summary__hindi hindi">{temple.hindi}</div>}
              </div>
              <div className="summary__rows">
                <div className="summary__row"><span>{t('Timings')}</span><span>{t(temple.timings)}</span></div>
                <div className="summary__row"><span>{t('Date')}</span><span>{fmtDate(date)}</span></div>
                <div className="summary__row"><span>{t('Slot')}</span><span>{slot || '—'}</span></div>
                <div className="summary__row"><span>{t('Devotees')}</span><span>{persons}</span></div>
              </div>
              <div className="summary__price">
                <span>{t('Pass fee')}</span>
                <strong>{t('Free')}</strong>
              </div>
              <div className="hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
                <Badge variant={temple.pass === 'official' ? 'soft' : 'success'}>{temple.pass === 'official' ? t('Pass via official portal') : t('No pass needed')}</Badge>
              </div>
              <p className="small muted">{t(temple.note)}</p>
            </div>
          </aside>
        </div>

        <div className="booking__extras">
          <TrustBar variant="light" items={BOOKING_TRUST.map((i) => ({ ...i, title: t(i.title), desc: t(i.desc) }))} />
          <div className="grid-2">
            <div className="card guidelines">
              <h3 className="h-card">{t('Darshan guidelines')}</h3>
              <ul className="feature-list">
                {GUIDELINES.map((g) => (
                  <li key={g.title}>
                    <span className="feature-list__icon"><Icon name={g.icon} size={18} /></span>
                    <div><div className="feature-list__title">{t(g.title)}</div><div className="feature-list__body">{t(g.desc)}</div></div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="card guidelines faq">
              <h3 className="h-card">{t('Frequently asked questions')}</h3>
              {FAQ.map((f, i) => (
                <details key={f.q} open={i === 0}>
                  <summary>{f.q}</summary>
                  <p>{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
